import {
  Day,
  PlaceType,
  Prisma,
  TouristicPlaceTimetable,
} from "@prisma/client";
import { Dates } from "./events.types";
import { CreateFile } from "./file.types";

export const touristicPlaceItemValues =
  Prisma.validator<Prisma.TouristicPlaceDefaultArgs>()({
    include: {
      address: true,
      image: true,
    },
  });

export type TouristicPlaceItemType = Prisma.TouristicPlaceGetPayload<
  typeof touristicPlaceItemValues
>;

export const touristicPlacePublicValues =
  Prisma.validator<Prisma.TouristicPlaceDefaultArgs>()({
    include: {
      address: true,
      openings: {
        include: {
          timetables: true,
        },
        orderBy: {
          fromDate: { sort: "asc", nulls: "first" },
        },
      },
      exceptionalOpenings: {
        include: {
          timetable: true,
        },
        orderBy: {
          date: "asc",
        },
      },
      image: true,
    },
  });

export type TouristicPlacePublicType = Prisma.TouristicPlaceGetPayload<
  typeof touristicPlacePublicValues
>;

export const touristicPlaceValues =
  Prisma.validator<Prisma.TouristicPlaceDefaultArgs>()({
    include: {
      ...touristicPlacePublicValues.include,
      manager: true,
    },
  });

export type TouristicPlaceType = Prisma.TouristicPlaceGetPayload<
  typeof touristicPlaceValues
>;

export const placeTypes = Object.values(PlaceType);

export const getPlaceTypeLabel = (type: PlaceType) => {
  switch (type) {
    case PlaceType.castle:
      return "Château";
    case PlaceType.monument:
      return "Monument";
    case PlaceType.museum:
      return "Musée";
    case PlaceType.other:
      return "Autre";
    case PlaceType.park:
      return "Parc";
    case PlaceType.viewpoint:
      return "Point de vue";
  }
};

export const getTimetableSchedules = ({
  openingTime,
  closingTime,
  breakStartTime,
  breakEndTime,
}: TouristicPlaceTimetable) => {
  const isOpen = openingTime && closingTime;
  if (!isOpen) return "Fermé";

  const hasBreak = breakStartTime && breakEndTime;
  if (!hasBreak) return `${openingTime} - ${closingTime}`;
  return `${openingTime} - ${breakStartTime}   ${breakEndTime} - ${closingTime}`;
};

interface Address {
  id: string;
  address: string;
  city: string;
  country: string;
  lat: string;
  long: string;
  name: string;
  zipCode: string;
  createdAt: string;
  updatedAt: string;
}

export interface TouristicPlaceSummary {
  id: string;
  type: string;
  name: string;
  description: string;
  address: Address;
  image?: CreateFile;
}

export type FilterTourtisticPlaceType = {
  name?: string;
};

export interface TouristicFilters {
  // events and places filters
  sortBy: "closest-dates" | "closest-to-me" | "recently-added";
  categories: "events" | "places";
  dates?: Dates;
  customDates: [Date, Date] | undefined;
  places: string[] | string;
  eventTypes: string[];
  placeTypes: string[];
  entryFree: boolean;
  entryPaid: boolean;
  venueIndoor: boolean;
  venueOutdoor: boolean;

  // exponent filters
  exponentSortBy: "alphabetical-asc" | "alphabetical-desc";
  exponentFilter: "all" | "current" | "past" | "not-scanned";
}

export interface FiltersExtended extends Partial<TouristicFilters> {
  searchText?: string;
}

export interface TouristicPlaceTimetableType {
  id: string;
  day: Day;
  from: string;
  to: string;
  openingTime: string;
  closingTime: string;
  breakStartTime: string;
  breakEndTime: string;
}

export interface TouristicPlaceOpening {
  allYear: boolean;
  id: string;
  fromDate: string;
  toDate: string;
  timetables: TouristicPlaceTimetableType[];
}

export interface TouristicPlaceDetail {
  id: string;
  type: PlaceType;
  name: string;
  description: string;
  isIndoor: boolean;
  isOutdoor: boolean;
  isPaidEntry: boolean;
  websiteUrl: string;
  address: Address;
  openings: TouristicPlaceOpening[];
  image?: CreateFile;
}
